<template>
	<div class="email-form">
		<VSTextField
			label="Email address"
			type="email"
			:disabled="submittingOrder"
			:value="email"
			autocomplete="email"
			:error-message="emailState && invalidEmailFeedback"
			@input="setEmailAndValidate"
		/>

		<VSWell v-if="suggestion" variant="info" size="sm">
			<p class="email-form__suggestion">
				<span
					>Did you mean
					<span class="email-form__email"
						>{{ suggestion.full }}?</span
					></span
				>
				<button class="email-form__action" @click="applySuggestion">
					Apply suggestion
				</button>
			</p>
		</VSWell>
	</div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { debounce } from 'lodash';
import emailSpellChecker from '@zootools/email-spell-checker';
import { emailRegex, checkEmailExists } from '@js/service/checkoutService';
import { VSTextField, VSWell } from '@component/UI';

export default {
	components: { VSTextField, VSWell },

	props: {
		hasError: {
			type: Boolean,
			required: true,
		},
	},

	data() {
		return {
			suggestion: undefined,
		};
	},

	computed: {
		...mapState('checkout', [
			'email',
			'emailExistsError',
			'submittingOrder',
		]),
		emailState() {
			if (this.hasError || this.emailExistsError) {
				this.scrollToTop();
				return true;
			}

			return null;
		},
		emailIsValid() {
			return this.email && emailRegex.test(this.email);
		},
		invalidEmailFeedback() {
			if (this.emailExistsError) {
				return 'This email address is already in use, please log in or use a different email address';
			}

			if (!this.email) {
				return 'Email is required';
			}

			if (!this.emailIsValid) {
				return 'Email is invalid';
			}

			return '';
		},
	},

	watch: {
		email: debounce(function () {
			this.suggestion = emailSpellChecker.run({
				email: this.email,
			});
		}, 500),
	},

	methods: {
		...mapMutations('checkout', [
			'setEmail',
			'setEmailExistsError',
			'setCheckingEmailExists',
		]),
		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		},
		// eslint-disable-next-line func-names
		debouncedCheckEmailExists: debounce(async function (email) {
			this.setEmailExistsError(await checkEmailExists(email));
			this.setCheckingEmailExists(false);
		}, 1000),
		setEmailAndValidate(email) {
			this.setEmail(email);

			this.setEmailExistsError(false);
			this.setCheckingEmailExists(true);

			if (this.emailIsValid) {
				this.debouncedCheckEmailExists(email);
			}
		},
		applySuggestion() {
			this.setEmail(this.suggestion.full);
		},
	},
};
</script>

<style lang="scss">
.email-form {
	display: flex;
	flex-direction: column;
	gap: var(--size-base);

	&__suggestion {
		display: flex;
		justify-content: space-between;
		gap: var(--size-sm);
	}

	&__email {
		font-weight: 700;
	}

	&__action {
		color: var(--blue-700);
		font-weight: 700;
		text-decoration: underline;
		cursor: pointer;
		background: transparent;
		font-family: inherit;
		border: none;
		padding: 0;
	}
}
</style>
